/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 * 
 ********************************************************************!*/

import { GetterTree } from "vuex";
import { IAdminState } from "./state";
import { IRootState } from "@/store/modules/root/state";
import { IGroupResult, IUserResult } from "@chatcaptain/types/organization";
import { useStore } from "@/store";
import { AdminStore } from "@/store/constants";
import { IChatbotResult } from "@/api/bot";
import { IInvoiceResult, IPaymentMethodResult, ISubscriptionResult } from "@chatcaptain/types/billing";
import { IPlan } from "@/store/types";
import { store } from "@/store";

export enum Getters {
  GET_USERS = "GET_USERS",
  GET_USER = "GET_USER",
  GET_GROUPS = "GET_GROUPS",
  GET_GROUP = "GET_GROUP",
  GET_CHATBOTS = "GET_CHATBOTS",
  GET_CHATBOT = "GET_CHATBOT",
  GET_SUBSCRIPTION = "GET_SUBSCRIPTION",
  GET_PERMISSION_NAME = "GET_PERMISSION_NAME",
  GET_PERMISSION_ORDER = "GET_PERMISSION_ORDER",
  GET_PAYMENT_METHODS = "GET_PAYMENT_METHODS",
  GET_INVOICES = "GET_INVOICES",
  GET_PLAN = "GET_PLAN"
}

export interface IGetters {
  [Getters.GET_USERS](state: IAdminState): IUserResult[];
  [Getters.GET_USER](state: IAdminState): (userId: string) => IUserResult | undefined;
  [Getters.GET_GROUPS](state: IAdminState): IGroupResult[];
  [Getters.GET_GROUP](state: IAdminState): (groupId: string) => IGroupResult | undefined;
  [Getters.GET_CHATBOTS](state: IAdminState): IChatbotResult[];
  [Getters.GET_CHATBOT](state: IAdminState, chatbotId: string): (chatbotId: string) => IChatbotResult | undefined;
  [Getters.GET_SUBSCRIPTION](state: IAdminState): ISubscriptionResult | null;
  [Getters.GET_PERMISSION_NAME](state: IAdminState): (permission: string) => string;
  [Getters.GET_PERMISSION_ORDER](state: IAdminState): (permission: string) => number;
  [Getters.GET_PAYMENT_METHODS](state: IAdminState): IPaymentMethodResult[];
  [Getters.GET_INVOICES](state: IAdminState): IInvoiceResult[];
  [Getters.GET_PLAN](state: IAdminState): (plan: string) => IPlan;
}

export const getters: GetterTree<IAdminState, IRootState> & IGetters = {
  [Getters.GET_USERS]: (state: IAdminState) => {
    return state.users;
  },
  [Getters.GET_USER]: (state: IAdminState) => (userId: string) => {
    if(!state.users || state.users.length <= 0) {
      store.dispatch(AdminStore.Actions.LOAD_USERS);
    }
    return state.users.find((el: IUserResult) => {
      return el.id === userId;
    });
  },
  [Getters.GET_GROUPS]: (state: IAdminState) => {
    return state.groups;
  },
  [Getters.GET_GROUP]: (state: IAdminState) => (groupId: string) => {
    if(!state.groups || state.groups.length <= 0) {
      store.dispatch(AdminStore.Actions.LOAD_GROUPS);
    }
    return state.groups.find((el: IGroupResult) => {
      return el.id === groupId;
    });
  },
  [Getters.GET_CHATBOTS]: (state: IAdminState) => {
    if(!state.chatbots || state.chatbots.length <= 0) {
      store.dispatch(AdminStore.Actions.LOAD_CHATBOTS);
    }
    return state.chatbots || [];
  },
  [Getters.GET_CHATBOT]: (state: IAdminState) => (chatbotId: string) => {
    if(!state.chatbots || state.chatbots.length <= 0) {
      store.dispatch(AdminStore.Actions.LOAD_CHATBOTS);
    }
    return state.chatbots.find((el: IChatbotResult) => {
      return el.id === chatbotId;
    });
  },
  [Getters.GET_SUBSCRIPTION]: (state: IAdminState) => {
    return state.subscription;
  },
  [Getters.GET_PERMISSION_NAME]: (state: IAdminState) => (permission: string) => {
    if(state.permissionNames[permission]) return state.permissionNames[permission];
    if(permission.startsWith("cc.chatbots.") && permission != "cc.chatbots.any"){
      const chatbotId = permission.split(".")[2];
      const chatbot = store.getters[AdminStore.Getters.GET_CHATBOT](chatbotId);
      if(chatbot && chatbot.name){
        return "Zugriff auf " + chatbot.name;
      } else {
        return "Zugriff auf ...";
      }
    }
    return "Unbekannt";
  },
  [Getters.GET_PERMISSION_ORDER]: (state: IAdminState) => (permission: string) => {
    const orderPos = state.permissionsOrder[permission];
    if(orderPos) return orderPos;
    return 999;
  },
  [Getters.GET_PAYMENT_METHODS]: (state: IAdminState) => {
    if(!state.paymentMethods || state.paymentMethods.length <= 0) {
      store.dispatch(AdminStore.Actions.LOAD_PAYMENT_METHODS);
    }
    return state.paymentMethods || [];
  },
  [Getters.GET_INVOICES]: (state: IAdminState) => {
    if(!state.invoices || state.invoices.length <= 0) {
      store.dispatch(AdminStore.Actions.LOAD_INVOICES);
    }
    return state.invoices || [];
  },
  [Getters.GET_PLAN]: (state: IAdminState) => (plan: string) => {
    return state.plans[plan] || {};
  },
}